/* eslint-disable no-unused-vars */
import Chart from 'react-apexcharts';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ArrowRightIcon from '../../../icons/ArrowRight';
import { useCallback, useState, useEffect } from 'react';
import useMounted from '../../../hooks/useMounted';
import moment from 'moment';
import useAuth from '../../../hooks/useAuth';
import { relaunchApi } from '../../../api/relaunchApi';
import PropTypes from 'prop-types';
import { getTone, toneAlpha, lightVeil, darkVeil, grey, greyAlpha } from '../../../theme/tones';
import usePermissions from '../../../hooks/usePermissions';

const RelaunchStats = (props) => {
  const { state } = props;
  const theme = useTheme();
  const mounted = useMounted();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [infoRelaunch, setInfoRelaunch] = useState([]);
  
  const correspondence = { 
    shift: 'Décalage', 
    turn1: 'Relance Tour 1', 
    turn2: 'Relance Tour 2', 
    fc: 'Formation continue' 
  };

  const stateThemes = {
    shift: {
      color: getTone('amber').base,
      bgLight: toneAlpha('amber', 0.08),
      bgDark: toneAlpha('amber', 0.15),
      gradient: `linear-gradient(90deg, ${getTone('amber').base} 0%, ${getTone('amber').color} 100%)`
    },
    turn1: {
      color: getTone('blue').base,
      bgLight: toneAlpha('blue', 0.08),
      bgDark: toneAlpha('blue', 0.15),
      gradient: `linear-gradient(90deg, ${getTone('blue').base} 0%, ${getTone('blue').color} 100%)`
    },
    turn2: {
      color: getTone('violet').base,
      bgLight: toneAlpha('violet', 0.08),
      bgDark: toneAlpha('violet', 0.15),
      gradient: `linear-gradient(90deg, ${getTone('violet').base} 0%, ${getTone('violet').color} 100%)`
    },
    fc: {
      color: getTone('emerald').base,
      bgLight: toneAlpha('emerald', 0.08),
      bgDark: toneAlpha('emerald', 0.15),
      gradient: `linear-gradient(90deg, ${getTone('emerald').base} 0%, ${getTone('emerald').color} 100%)`
    }
  };

  const getRelaunch = useCallback(async () => {
    try {
      const data = await relaunchApi.getRelaunch(state, localStorage.getItem('accessToken'));
      if (data.length === 0) {
        throw new Error('Aucune relance possible');
      }

      if (mounted.current) {
        setInfoRelaunch(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [mounted, state]);

  useEffect(() => {
    getRelaunch();
  }, [getRelaunch]);

  return (
    <Box sx={{ py: 1 }}>
      <Grid container spacing={3}>
        {infoRelaunch.map((info) => {
          const activeTheme = stateThemes[info.state] || {
            color: theme.palette.primary.main,
            bgLight: toneAlpha('blue', 0.08),
            bgDark: toneAlpha('blue', 0.15),
            gradient: `linear-gradient(90deg, ${getTone('blue').base} 0%, ${getTone('blue').color} 100%)`
          };

          const chartOptions = {
            chart: {
              background: 'transparent',
              stacked: false,
              toolbar: {
                show: false
              }
            },
            colors: [activeTheme.color],
            labels: ['Progression'],
            plotOptions: {
              radialBar: {
                dataLabels: {
                  name: {
                    show: true,
                    fontSize: '10px',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    offsetY: -8
                  },
                  value: {
                    show: true,
                    fontSize: '18px',
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    offsetY: 6,
                    formatter: (val) => `${val}%`
                  }
                },
                hollow: {
                  size: '62%'
                },
                track: {
                  background: theme.palette.mode === 'light' ? grey(100) : lightVeil(0.05)
                }
              }
            },
            theme: {
              mode: theme.palette.mode
            }
          };
          
          const chartSeries = [info.progression === null ? 0 : info.progression];
          
          let isUrgent = false;
          const limitDate = new Date(info.end_date);
          if (new Date() >= limitDate.setDate(limitDate.getDate() - 20)) {
            isUrgent = true;
          }

          return (
            ((info.nbCustomers !== '0' && can('relaunch.process', user.id_service === 4)) || can('relaunch.manage', user.access_level === 0)) && (
              <Grid
                item
                xs={12}
                md={6}
                key={`${info.state}-${info.end_date}`}
              >
                <Card
                  sx={{
                    borderRadius: '24px',
                    border: '1px solid',
                    borderColor: isUrgent 
                      ? toneAlpha('rose', 0.4) 
                      : (theme.palette.mode === 'light' ? greyAlpha(200, 0.8) : lightVeil(0.05)),
                    backgroundColor: 'background.paper',
                    backdropFilter: 'blur(20px)',
                    boxShadow: theme.palette.mode === 'light'
                      ? `0 8px 32px ${toneAlpha('neutral', 0.05)}`
                      : `0 8px 32px ${darkVeil(0.15)}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: isUrgent ? `linear-gradient(90deg, ${getTone('rose').base}, ${getTone('orange').base})` : activeTheme.gradient,
                      opacity: 0.85
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: isUrgent 
                        ? `0 20px 40px ${toneAlpha('rose', 0.12)}` 
                        : `0 20px 40px ${toneAlpha('blue', 0.08)}`,
                      borderColor: isUrgent ? toneAlpha('rose', 0.6) : activeTheme.color,
                    }
                  }}
                >
                  <CardContent
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 3,
                      p: 3.5,
                      flexGrow: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Chart
                        height="160"
                        options={chartOptions}
                        series={chartSeries}
                        type="radialBar"
                        width="160"
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mt: -1.5 }}>
                        Semaine en cours
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1,
                        textAlign: { xs: 'center', sm: 'left' }
                      }}
                    >
                      <Typography
                        color="text.primary"
                        variant="h6"
                        sx={{ fontWeight: 800, mb: 1, lineHeight: 1.3, letterSpacing: '-0.01em' }}
                      >
                        {`Sessions terminant le ${moment(info.end_date).format('DD/MM/YYYY')}`}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' }, alignItems: 'center', mt: 0.5 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            backgroundColor: theme.palette.mode === 'light' ? activeTheme.bgLight : activeTheme.bgDark,
                            color: activeTheme.color
                          }}
                        >
                          {correspondence[info.state]}
                        </Box>
                        
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            backgroundColor: theme.palette.mode === 'light' ? greyAlpha(900, 0.04) : lightVeil(0.04),
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {`${info.nbCustomers} client${parseInt(info.nbCustomers, 10) > 1 ? 's' : ''}`}
                        </Box>

                        {isUrgent && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              backgroundColor: theme.palette.mode === 'light' ? toneAlpha('rose', 0.08) : toneAlpha('rose', 0.15),
                              color: 'error.main',
                              border: '1px solid',
                              borderColor: 'transparent'
                            }}
                          >
                            Urgent
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      px: 3, 
                      py: 2,
                      bgcolor: theme.palette.mode === 'light' ? greyAlpha(50, 0.4) : lightVeil(0.01),
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Button
                      component={RouterLink}
                      to={`/dashboard/relaunch/${info.state}/${info.end_date}`}
                      color="primary"
                      endIcon={
                        <ArrowRightIcon 
                          fontSize="small" 
                          sx={{ 
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' 
                          }} 
                        />
                      }
                      variant="text"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: 'primary.dark',
                          '& .MuiSvgIcon-root': {
                            transform: 'translateX(4px)'
                          }
                        }
                      }}
                    >
                      Plus d&apos;informations
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          );
        })}
      </Grid>
    </Box>
  );
};

RelaunchStats.propTypes = {
  state: PropTypes.string.isRequired
};

export default RelaunchStats;
